import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Profile {
  name: string | null;
  ramadanStartDate: string | null;
  preferences: Record<string, unknown>;
}

export const useProfile = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const loadProfile = async () => {
      if (isGuest || !user) {
        const saved = localStorage.getItem('ramadanProfile');
        if (saved) {
          try {
            setProfile(JSON.parse(saved));
          } catch {
            setProfile(null);
          }
        }
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setProfile({
            name: data.name,
            ramadanStartDate: data.ramadan_start_date,
            preferences: (data.preferences as Record<string, unknown>) || {}
          });
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      setIsLoading(false);
    };

    loadProfile();
  }, [user, isGuest]);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    const newProfile = { ...profile, ...updates } as Profile;
    setProfile(newProfile);

    if (isGuest || !user) {
      localStorage.setItem('ramadanProfile', JSON.stringify(newProfile));
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: newProfile.name,
          ramadan_start_date: newProfile.ramadanStartDate,
          preferences: newProfile.preferences as unknown as Record<string, never>
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: 'Profile updated',
        description: 'Your settings have been saved.'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error saving',
        description: 'Your profile could not be updated.',
        variant: 'destructive'
      });
    }
  }, [user, isGuest, profile, toast]);

  return {
    profile,
    isLoading,
    updateProfile
  };
};
